import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { IMG } from "@/lib/data";
import { getState, setProfile, subscribe, takeNewBadges } from "@/lib/progress";
import { speak } from "@/lib/speech";

export const AVATARS = [
  { key: "boy", label: "Boy", img: IMG.avatar_boy, emoji: "👦" },
  { key: "girl", label: "Girl", img: IMG.avatar_girl, emoji: "👧" },
];

export function AvatarImg({ avatar, className = "w-16 h-16" }) {
  const a = AVATARS.find(x => x.key === avatar) || AVATARS[0];
  return <img src={a.img} alt={a.label} className={`${className} object-contain`} onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { textContent: a.emoji, className: "text-5xl" })); }} />;
}

/* Profile form used by first-run modal and Parent page */
export function ProfileForm({ onSaved, compact }) {
  const p = getState().profile;
  const [name, setName] = useState(p.name || "");
  const [avatar, setAvatar] = useState(p.avatar || "boy");
  const save = () => {
    const n = name.trim() || "Little Star";
    setProfile({ name: n, avatar });
    speak(`Hi ${n}! Let's learn together!`);
    onSaved?.();
  };
  return (
    <div className={compact ? "" : "text-center"}>
      <div className={`grid grid-cols-2 gap-4 ${compact ? "max-w-xs" : "max-w-sm mx-auto"} mt-4`}>
        {AVATARS.map(a => (
          <button key={a.key} data-testid={`avatar-${a.key}`} onClick={() => setAvatar(a.key)} className={`ll-tile bg-white flex flex-col items-center p-3 ${avatar === a.key ? "ring-4 ring-purple-400 bg-purple-50" : ""}`}>
            <AvatarImg avatar={a.key} className="w-24 h-24" />
            <div className="font-display font-bold text-purple-900 mt-1">{a.label}</div>
          </button>
        ))}
      </div>
      <input data-testid="profile-name-input" value={name} onChange={e => setName(e.target.value)} maxLength={16} placeholder="What's your name?"
        className={`mt-5 w-full ${compact ? "max-w-xs" : "max-w-sm"} font-display font-bold text-2xl text-center text-purple-900 rounded-2xl border-4 border-purple-200 focus:border-purple-400 outline-none px-4 py-3 bg-white`} />
      <div className="mt-5"><button data-testid="profile-save-button" onClick={save} className="ll-btn ll-btn-primary text-lg">{compact ? "Save" : "Let's go! 🚀"}</button></div>
    </div>
  );
}

export function ProfileSetupModal() {
  const loc = useLocation();
  const [open, setOpen] = useState(!getState().profile.setup);
  if (!open || loc.pathname !== "/") return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-purple-900/40 backdrop-blur-sm p-4" data-testid="profile-setup-modal">
      <div className="ll-card p-6 sm:p-8 max-w-md w-full text-center ll-pop">
        <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Welcome to LittleLearner</div>
        <h2 className="font-display font-bold text-3xl text-purple-900 mt-1">Who's learning today?</h2>
        <ProfileForm onSaved={() => setOpen(false)} />
      </div>
    </div>
  );
}

/* Toast shown when a new badge is earned */
export function BadgeToast() {
  const [queue, setQueue] = useState([]);
  useEffect(() => subscribe(() => { const b = takeNewBadges(); if (b.length) setQueue(q => [...q, ...b]); }), []);
  useEffect(() => { if (queue.length) { speak(`New badge! ${queue[0].title}`); const t = setTimeout(() => setQueue(q => q.slice(1)), 3500); return () => clearTimeout(t); } }, [queue]);
  if (!queue.length) return null;
  const b = queue[0];
  return (
    <div className="fixed top-20 inset-x-0 z-[70] flex justify-center px-4 pointer-events-none">
      <div className="ll-card ll-slide-in px-5 py-3 flex items-center gap-3 bg-gradient-to-r from-yellow-50 to-amber-100 border-yellow-300" data-testid="badge-toast">
        <div className="text-4xl">{b.emoji}</div>
        <div><div className="text-xs uppercase tracking-widest font-bold text-amber-700">New badge · +3 ⭐</div><div className="font-display font-bold text-lg text-purple-900">{b.title}</div></div>
      </div>
    </div>
  );
}
